import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    API_URL,
    GET_REFERENCE_FORM_DETAILS_URL,
    POST_REFERENCE_CONFIRM_DETAILS,
    POST_REFERENCE_UPDATE_DETAILS,
    POST_REFERENCE_ABOUT_YOU,
    POST_REFERENCE_ANSWERS,
    POST_REFERENCE_POOLS,
    POST_REFERENCE_DECLINE,
    POST_REFERENCE_FILE_UPLOAD,
} from '../../../../constants/api';
import {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
} from '../../../../utils/safe-storage';

// in-progress answers are persisted per-token so a refresh doesn't lose them
export const answersStorageKey = (token) => `refnow-reference-answers-${token}`;

// remembers that this browser session declined, so a refresh shows the same
// friendly success screen instead of the generic "request declined" error
const declinedStorageKey = (token) => `refnow-reference-declined-${token}`;

export const REFERENCE_FORM_STEPS = Object.freeze({
    WELCOME: 1,
    CONFIRM_DETAILS: 2,
    EDIT_DETAILS: 3,
    ABOUT_YOU: 4,
    QUESTIONS: 5,
    POOLS: 6,
    VERIFY_ID: 7,
    THANK_YOU: 8,
    PDF_UPLOAD: 9,
    PDF_SUCCESS: 10,
    DECLINE: 11,
    ALREADY_COMPLETE: 12,
    ERROR: 13,
});

// the three segments shown in the header progress bar for the main journey
// the final review (answers list + comment box) lives inside the Questions
// screen rather than being a navigable REFERENCE_FORM_STEPS entry, so a
// sentinel keys its progress segment
export const PROGRESS_REVIEW_STEP = 'review';

export const PROGRESS_STEPS = [
    REFERENCE_FORM_STEPS.CONFIRM_DETAILS,
    REFERENCE_FORM_STEPS.ABOUT_YOU,
    REFERENCE_FORM_STEPS.QUESTIONS,
    PROGRESS_REVIEW_STEP,
];

// label message ids parallel to PROGRESS_STEPS, rendered under the progress bar
export const PROGRESS_STEP_LABEL_IDS = [
    'Forms.reference.progress.confirmDetails',
    'Forms.reference.progress.aboutYou',
    'Forms.reference.progress.questions',
    'Forms.reference.progress.review',
];

const initialState = {
    loading: true,
    submitting: false,
    token: '',
    isCompany: 0,
    currentStep: REFERENCE_FORM_STEPS.WELCOME,
    error: {},
    details: {},
    declined: false,
};

// where the journey goes once the reference answers (and any pool opt-ins) are in
const stepAfterSubmission = (details) =>
    details.skip_verify_ref == 0
        ? REFERENCE_FORM_STEPS.VERIFY_ID
        : REFERENCE_FORM_STEPS.THANK_YOU;

const referenceFormSlice = createSlice({
    name: 'reference',
    initialState,
    reducers: {
        setCurrentStep: (state, action) => {
            state.currentStep = action.payload;
            state.error = {};
        },
        setReferenceToken: (state, action) => {
            state.token = action.payload;
        },
        setIsCompany: (state, action) => {
            state.isCompany = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getReferenceDetails.pending, (state) => {
            const isInitialLoad = !state.details || Object.keys(state.details).length === 0;
            if (isInitialLoad) {
                state.loading = true;
            }
        });
        builder.addCase(getReferenceDetails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || { errorType: 'unknown', message: 'An error occurred' };
            state.currentStep = REFERENCE_FORM_STEPS.ERROR;
        });
        builder.addCase(getReferenceDetails.fulfilled, (state, action) => {
            const { currentStep, justDeclined, ...details } = action.payload;

            state.loading = false;
            state.details = details;
            state.currentStep = currentStep;

            if (justDeclined) {
                state.declined = true;
            }
        });

        const submittingThunks = [
            confirmCandidateDetails,
            updateCandidateDetails,
            submitAboutYou,
            submitAnswers,
            submitPools,
            submitDecline,
            uploadPdfReference,
        ];
        submittingThunks.forEach((thunk) => {
            builder.addCase(thunk.pending, (state) => {
                state.submitting = true;
                state.error = {};
            });
            builder.addCase(thunk.rejected, (state, action) => {
                state.submitting = false;
                state.error = { inputs: action.payload };
            });
        });

        builder.addCase(confirmCandidateDetails.fulfilled, (state) => {
            state.submitting = false;
            state.currentStep = REFERENCE_FORM_STEPS.ABOUT_YOU;
        });
        builder.addCase(updateCandidateDetails.fulfilled, (state, action) => {
            state.submitting = false;
            state.details = { ...state.details, ...action.payload };
            state.currentStep = REFERENCE_FORM_STEPS.ABOUT_YOU;
        });
        builder.addCase(submitAboutYou.fulfilled, (state, action) => {
            state.submitting = false;
            state.details.referee_relationship = action.payload.relationship;
            state.currentStep = REFERENCE_FORM_STEPS.QUESTIONS;
        });
        builder.addCase(submitAnswers.fulfilled, (state) => {
            state.submitting = false;
            state.currentStep =
                state.details.talentpool == 1 || state.details.vacancypool == 1
                    ? REFERENCE_FORM_STEPS.POOLS
                    : stepAfterSubmission(state.details);
        });
        builder.addCase(submitPools.fulfilled, (state) => {
            state.submitting = false;
            state.currentStep = stepAfterSubmission(state.details);
        });
        builder.addCase(submitDecline.fulfilled, (state) => {
            state.submitting = false;
            state.declined = true;
        });
        builder.addCase(uploadPdfReference.fulfilled, (state) => {
            state.submitting = false;
            state.currentStep =
                state.isCompany == 1
                    ? REFERENCE_FORM_STEPS.THANK_YOU
                    : REFERENCE_FORM_STEPS.PDF_SUCCESS;
        });
    },
});

// fetch the reference request and decide which screen to land on,
// mirroring the old form's page= deep links and completion checks
export const getReferenceDetails = createAsyncThunk(
    'reference/getReferenceDetails',
    async (data, thunkAPI) => {
        const { token, page = '' } = data;
        const resolvedToken = token || thunkAPI.getState().forms.reference.token;

        try {
            const url = `${API_URL}${GET_REFERENCE_FORM_DETAILS_URL}`;
            const config = {
                params: {
                    token: resolvedToken,
                },
                withCredentials: true,
            };

            const response = await axios.get(url, config);

            if (response?.data?.status === 'error' || !response?.data?.data?.[0]) {
                return thunkAPI.rejectWithValue({
                    errorType: 'not_found',
                    message: response?.data?.message || 'Request not found',
                });
            }

            const details = response.data.data[0];

            if (details.declinestatus > 0) {
                // a refresh right after declining keeps the in-place success screen
                if (getSessionStorageItem(declinedStorageKey(resolvedToken))) {
                    return {
                        ...details,
                        currentStep: REFERENCE_FORM_STEPS.DECLINE,
                        justDeclined: true,
                    };
                }

                return thunkAPI.rejectWithValue({
                    errorType: 'declined',
                    message: 'This request has been declined',
                });
            }

            const isComplete =
                details.completeddate !== '0000-00-00 00:00:00' ||
                details.ref_file_added_date !== '0000-00-00 00:00:00';

            let currentStep = REFERENCE_FORM_STEPS.WELCOME;
            if (page === 'verify') {
                currentStep = REFERENCE_FORM_STEPS.VERIFY_ID;
            } else if (isComplete) {
                currentStep = REFERENCE_FORM_STEPS.ALREADY_COMPLETE;
            } else if (page === 'decline') {
                currentStep = REFERENCE_FORM_STEPS.DECLINE;
            } else if (page === 'upload') {
                currentStep = REFERENCE_FORM_STEPS.PDF_UPLOAD;
            }

            return { ...details, currentStep };
        } catch (error) {
            console.error('Error getting reference details:', error);
            return thunkAPI.rejectWithValue({
                errorType: 'network',
                message: 'Unable to load this request. Please try again later.',
            });
        }
    }
);

// the referee confirmed the candidate-provided details are correct
export const confirmCandidateDetails = createAsyncThunk(
    'reference/confirmCandidateDetails',
    async (_, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_REFERENCE_CONFIRM_DETAILS}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.reference.token);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            return { success: true };
        } catch (error) {
            console.error('Error confirming details:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// the referee corrected the candidate-provided details
export const updateCandidateDetails = createAsyncThunk(
    'reference/updateCandidateDetails',
    async (data, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_REFERENCE_UPDATE_DETAILS}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.reference.token);
            params.append('inputfullname', data.fullName);
            params.append('inputcompany', data.company || '');
            params.append('inputjobtitle', data.jobTitle || '');
            params.append('inputschool', data.school || '');
            params.append('inputcourse', data.course || '');
            params.append('inputstartday', data.startDay);
            params.append('inputstartmonth', data.startMonth);
            params.append('inputstartyear', data.startYear);
            params.append('inputendday', data.endDay);
            params.append('inputendmonth', data.currentlyPresent ? 0 : data.endMonth);
            params.append('inputendyear', data.endYear);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            // reflect the corrections locally so later screens show the right names
            return {
                refereename: data.fullName,
                refereecompany: data.company,
                can_jobtitle: data.jobTitle,
                edu_institution: data.school,
                edu_course: data.course,
            };
        } catch (error) {
            console.error('Error updating details:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// job title, phone and relationship
export const submitAboutYou = createAsyncThunk(
    'reference/submitAboutYou',
    async ({ jobTitle, phone, phoneType, relationship }, thunkAPI) => {
        try {
            const { token, details } = thunkAPI.getState().forms.reference;
            const url = `${API_URL}${POST_REFERENCE_ABOUT_YOU}`;
            const params = new URLSearchParams();
            params.append('inputtoken', token);
            params.append(
                'inputjobtitle',
                jobTitle === '' && details.ispersonal == 1 ? 'N/A' : jobTitle
            );
            params.append('inputphone', phone);
            params.append('inputphonetype', phoneType);
            params.append('inputrelationship', relationship);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            return { success: true, relationship };
        } catch (error) {
            console.error('Error submitting about you:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// the reference answers themselves, matching the old form's step3 wire format
export const submitAnswers = createAsyncThunk(
    'reference/submitAnswers',
    async ({ answers, comments = '' }, thunkAPI) => {
        try {
            const { token, details } = thunkAPI.getState().forms.reference;
            const url = `${API_URL}${POST_REFERENCE_ANSWERS}`;
            const params = new FormData();
            params.append('inputtoken', token);
            params.append('inputispersonalquestions', details.ispersonalquestions);
            params.append('inputiseducationalquestions', details.iseducationalquestions);
            params.append('inputqcomments', comments);

            Object.entries(answers).forEach(([rqid, value]) => {
                if (value instanceof File) {
                    params.append(`inputquestion[${rqid}]`, value, value.name);
                } else {
                    params.append(`inputquestion[${rqid}]`, value ?? '');
                }
            });

            const config = {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            };

            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            // the saved in-progress answers are no longer needed
            removeSessionStorageItem(answersStorageKey(token));

            return { success: true };
        } catch (error) {
            console.error('Error submitting answers:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// optional talent pool / vacancy pool opt-ins
export const submitPools = createAsyncThunk(
    'reference/submitPools',
    async ({ talentPool, personalEmail = '', vacancyPool, vacancyRoles = '' }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_REFERENCE_POOLS}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.reference.token);
            params.append('inputtalentpool', talentPool ? 1 : 0);
            params.append('inputpersonalemail', personalEmail);
            params.append('inputvacancypool', vacancyPool ? 1 : 0);
            params.append('inputvacancyroles', vacancyRoles);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            return { success: true };
        } catch (error) {
            console.error('Error submitting pools:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// the referee is unable to provide the reference
export const submitDecline = createAsyncThunk(
    'reference/submitDecline',
    async ({ reason }, thunkAPI) => {
        try {
            const { token } = thunkAPI.getState().forms.reference;
            const url = `${API_URL}${POST_REFERENCE_DECLINE}`;
            const params = new URLSearchParams();
            params.append('inputtoken', token);
            params.append('inputdeclinereason', reason);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            setSessionStorageItem(declinedStorageKey(token), '1');

            return { success: true };
        } catch (error) {
            console.error('Error declining reference:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// upload a previously written reference instead of answering questions
export const uploadPdfReference = createAsyncThunk(
    'reference/uploadPdfReference',
    async ({ file, password = '', startDate, endDate, currentlyPresent }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_REFERENCE_FILE_UPLOAD}`;
            const params = new FormData();
            params.append('inputtoken', thunkAPI.getState().forms.reference.token);
            params.append('inputfile', file);
            params.append('inputpdfpassword', password);
            params.append('inputstartday', startDate.day);
            params.append('inputstartmonth', startDate.month);
            params.append('inputstartyear', startDate.year);
            params.append('inputendday', currentlyPresent ? '0' : endDate.day);
            params.append('inputendmonth', currentlyPresent ? '0' : endDate.month);
            params.append('inputendyear', currentlyPresent ? '0' : endDate.year);

            const config = {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            };

            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            return { success: true };
        } catch (error) {
            console.error('Error uploading reference file:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

export const { setCurrentStep, setReferenceToken, setIsCompany, setError } =
    referenceFormSlice.actions;

export default referenceFormSlice.reducer;
